import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Search,
  Calendar,
  Clock,
  ExternalLink,
  Eye,
  ThumbsUp,
  MessageCircle,
  Star,
  Filter,
  ArrowRight,
} from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { useToast } from "@/hooks/use-toast";
import { handleSubscribe } from "@/utils/subscribeHandler";
import Spinner from "@/components/ui/Spinner";
import NoDataCard from "@/components/ui/NoDataCard";
import { BlogCardSkeleton } from "@/components/ui/BlogCardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";

const Blog = () => {
  const { blogs, loading } = useData();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  // if (loading) {
  //   return <Spinner size="60px" />;
  // }
  if (loading) {
  return (
    <div className="container mx-auto py-12">
      {/* Header Skeleton */}
      <div className="text-center mb-16 space-y-4">
        <Skeleton className="h-12 w-64 mx-auto" />
        <Skeleton className="h-6 w-96 mx-auto" />
      </div>

      {/* Featured Skeleton */}
      <div className="grid lg:grid-cols-3 gap-8 mb-16">
        {[1, 2, 3].map((i) => (
          <BlogCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}
  // Get all unique tags
  const allTags = ["All", ...new Set(blogs.flatMap((blog) => blog.tags || []))];

  // Filter and sort blogs
  const filteredBlogs = blogs
    .filter((blog) => {
      const matchesSearch =
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTag =
        selectedTag === "All" || blog.tags.includes(selectedTag);
      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      if (sortBy === "newest") {
        return new Date(b.publishedAt) - new Date(a.publishedAt);
      } else if (sortBy === "featured") {
        return b.featured - a.featured;
      }
      return 0;
    });

  const featuredBlogs = blogs.filter((blog) => blog.featured);

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubscribe(email, setEmail, toast);
  };

  const BlogCard = ({ blog, featured = false }) => (
    <Card
      className={`group hover:shadow-xl transition-all duration-300 overflow-hidden ${
        featured ? "lg:col-span-2" : ""
      }`}
    >
      <div
        className={`aspect-video overflow-hidden ${
          featured ? "lg:aspect-[21/9]" : ""
        }`}
      >
        <img
          src={blog?.images[0]?.url}
          alt={blog.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          {blog.featured && (
            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
              <Star className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-center text-sm text-muted-foreground mb-3">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{new Date(blog.createdAt).toLocaleDateString()}</span>
              <span className="mx-2">•</span>
              <Clock className="w-4 h-4 mr-1" />
              <span>{blog.readTime}</span>
              <span className="mx-2">•</span>
              <Eye className="w-4 h-4 mr-1" />
              <span>{Math.floor(Math.random() * 1000) + 100}</span>
            </div>
            <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-600 transition-colors line-clamp-2">
              {blog.title}
            </h3>
            <p className="text-muted-foreground text-sm line-clamp-3">
              {blog.excerpt}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {blog.tags.slice(0, featured ? 5 : 3).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {blog.tags.length > (featured ? 5 : 3) && (
              <Badge variant="outline" className="text-xs">
                +{blog.tags.length - (featured ? 5 : 3)} more
              </Badge>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <ThumbsUp className="w-4 h-4" />
                <span>{Math.floor(Math.random() * 50) + 10}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span>{Math.floor(Math.random() * 20) + 5}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" asChild>
                <a href={blog.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Medium
                </a>
              </Button>
              <Button
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
                asChild
              >
                <Link to={`/blog/${blog?._id}`}>
                  Read More
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            My{" "}
            <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
              Blog
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Insights, tutorials, and thoughts on web development, data
            engineering, and technology trends.
          </p>
        </div>

        {/* Featured Posts */}
        {featuredBlogs.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-8">Featured Posts</h2>
            <div className="grid lg:grid-cols-3 gap-8">
              {featuredBlogs.map((blog, index) => (
                <BlogCard key={blog?._id} blog={blog} featured={index === 0} />
              ))}
            </div>
          </section>
        )}

        {/* Search and Filters */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Filter:</span>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="bg-background border border-input rounded px-3 py-1 text-sm"
                >
                  {allTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-background border border-input rounded px-3 py-1 text-sm"
                >
                  <option value="newest">Newest First</option>
                  <option value="featured">Featured First</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-semibold">
              All Posts ({filteredBlogs.length})
            </h2>
          </div>

          {filteredBlogs.length > 0 ? (
            <div className="grid lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <BlogCard key={blog?._id} blog={blog} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search or filter criteria.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedTag("All");
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </section>

        {/* Newsletter Signup */}
        <section className="mt-20 py-16 bg-gradient-to-r from-purple-50 to-black/5 dark:from-purple-950/20 dark:to-black/40 rounded-3xl text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Subscribe to get notified about new blog posts and insights on web
            development and data engineering.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <form onSubmit={onSubmit} className="space-y-4 w-3/5">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="text-sm"
                required
              />
              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                Subscribe
                <ArrowRight className="ml-2 h-3 w-3" />
              </Button>
            </form>
          </div>
        </section>

        {/* Medium CTA */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Read More on Medium</h2>
          <p className="text-muted-foreground mb-6">
            I regularly publish technical articles and insights on Medium.
            Follow me for more content!
          </p>
          <Button variant="outline" size="lg" asChild>
            <a
              href="https://medium.com/@bakhtawarfahim10"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Follow on Medium
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Blog;
